// R81 — **THE ONE THING EVERY MODAL ON THIS SITE IS DRAWN INSIDE**, and nothing could reach it.
//
// It moved as this round's line payment, and it is the payment this line likes best: **the thing
// that moved is the thing that needed an instrument.** R40's print law is ABOUT this component —
// *"`ModalBackdrop` PORTALS TO `<body>` TOO, so it was a SIBLING of `#root` and was never inside the
// thing being hidden: `position: fixed`, and Chrome puts a fixed element on page one, so the dark
// modal a report is read in printed over page one of the white report"* — and that whole finding was
// held by a real Chrome with print media emulated, because the component itself sat in a 12,000-line
// TSX file where no gate could name it.
//
// **IT HOLDS TWO EFFECTS AND CAME ANYWAY, ON R10-3's RULING AS R67 RESTATED IT: a hook only blocks a
// component a gate CALLS.** R36 kept `LegalModal` in App.tsx for exactly the reason R67 then retired
// — that `createPortal` was something "the gates' stub jsx-runtime has no answer for", which was
// false: `loadTsx` stubs exactly two specifiers, so `react-dom` bundles like any other dependency.
// Out here the class name, the portal target and the two ways out are readable by an importing gate
// rather than by a browser.
//
// THE TWO EFFECTS ARE ONE RULING EACH AND NEITHER IS OPTIONAL:
//   * ESCAPE CLOSES. The X and Escape are the ways out; a stray tap beside the panel is not, because
//     reading a card should never be lost to one.
//   * OV2-9 — NAVIGATION SUPERSEDES EVERYTHING (owner's override, 2026-07-29). A bottom-bar tab goes
//     where it says and takes WHATEVER is up with it, reading modals included. Her ruling overrides
//     the earlier paid-output exception on this one point, and the reason is that the exception cost
//     more than it protected: every reading is on its shelf the moment it finishes (the no-hostage
//     law), so dismissing one loses nothing, while a modal that outlives a tap makes the house feel
//     stuck. MEASURED BEFORE THE FIX: tapping Home or Ask Lala only *appeared* to dismiss the
//     saved-report modal, because navigating away unmounted the page that owned it. Tap My Room while
//     already in My Room and the modal sat there through the navigation.
//
// **`onClose` IS THE OPT-IN, AND ITS ABSENCE IS DELIBERATE RATHER THAN FORGOTTEN** (R27's optional-
// prop shape, argued instead of drifted): a backdrop with no `onClose` — the onboarding gate, the
// room-build progress — is a modal that must not be dismissible, and it is dismissible by NEITHER
// route because both effects read the same prop.
import { useEffect } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { CLOSE_OVERLAYS_EVENT } from "./MobileNav";
import { useEscapeKey } from "./useEscapeKey";

export function ModalBackdrop({ label, className, onClose, children }: { label: string; className?: string; onClose?: () => void; children: ReactNode }) {
  // R82: the presence guard IS the law's own argument now — `undefined` attaches no listener — so
  // the site's one backdrop keeps the site's one Escape promise rather than its own copy of it.
  useEscapeKey(onClose);
  // OV2-9's half. It lives on the shared backdrop, so a modal added tomorrow obeys by passing
  // `onClose` rather than by somebody remembering.
  useEffect(() => {
    if (!onClose) return;
    const step = () => onClose();
    window.addEventListener(CLOSE_OVERLAYS_EVENT, step);
    return () => window.removeEventListener(CLOSE_OVERLAYS_EVENT, step);
  }, [onClose]);
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className={className ? `modal-backdrop ${className}` : "modal-backdrop"} role="dialog" aria-modal="true" aria-label={label}>
      {children}
    </div>,
    document.body,
  );
}
